<script lang="ts" setup>
/* eslint-disable vue/no-v-html */
import type { PageHeaderMenuItem } from '~/components/page/header/menu/PageHeaderMenu.vue'
import { ApiCorporationContacts } from '~~/server/api/corporation/contacts'

const route = useRoute()
const { data: page, redirect } = await useFetchWithRedirect<ApiCorporationContacts>('/api/corporation/contacts', { route })

if(redirect && !redirect?.error) {
    navigateTo(redirect.data.url, redirect.data.options)
}

const sections = ref<PageHeaderMenuItem[]>([
    { title: 'Zákaznícke linky', hash: '#call-centrum' },
    { title: 'Ďalšie kontakty', hash: '#asistencne-sluzby' },
    { title: 'Napíšte nám', hash: '#napiste-nam' },
    { title: 'Naše pobočky a servisy', hash: '#nase-pobocky-a-servisy' },
])

// Schema.org
useSchemaOrg([
    defineWebPage({
        name: page.value?.data.title,
        primaryImageOfPage:
            page.value?.data.headline.image.path['1540x0']?.url,
    }),
])

// Page meta
usePageMeta({
    meta: page.value?.data.meta,
    title: page.value?.data.title,
})

</script>
<template>
  <article v-if="page">
    <ContainerFluid>
      <Breadcrumbs
        :current="page.data.title"
        :links="[]"
      />
    </ContainerFluid>
    <PageHeader
      class="mb-12 mb-md-20"
      :headline="page.data.headline"
      :subtitle="page.data.headline.subtitle"
      :title="page.data.title"
      size="normal"
      :sections="sections"
      :cta="page.data.headline.cta"
    />

    <ContainerFluid>
      <Alert
        v-if="page.data.info"
        color="info"
        class="mb-4 mb-md-6"
      >
        <svg
          aria-hidden="true"
          width="16"
          height="16"
          class="d-block text-secondary me-2 me-md-4 flex-shrink-0"
        >
          <use xlink:href="#info" />
        </svg>
        <div v-html="page.data.info" />
      </Alert>
      <section
        id="call-centrum"
        class="mb-12 mb-md-20 scroll-margin-top-9"
      >
        <LazyPagesContactsCallcenter
          :data="page.data.call_centrum"
          :note="page.data.note"
          class="scroll-margin-top-20"
        />
      </section>

      <LazyPagesContactsAssistenceServices
        id="asistencne-sluzby"
        :data="page.data.misc_contacts"
        class="mb-12 mb-md-20 scroll-margin-top-20"
      />
      <LazyWidgetEmailUs
        id="napiste-nam"
        class="mb-12 mb-md-20 scroll-margin-top-11"
      />
      <PagesContactsMap
        id="nase-pobocky-a-servisy"
        :title="$t('Naše pobočky a servisy')"
        class="mb-12 mb-md-20 scroll-margin-top-12"
      >
        <template
          v-if="page.data.office_warning"
          #offices-alert
        >
          <Alert
            color="info"
            class="mb-10"
          >
            <svg
              class="d-block me-2 text-secondary"
              width="16"
              height="16"
              aria-hidden="true"
            >
              <use xlink:href="#alert" />
            </svg>
            {{ page.data.office_warning }}
          </Alert>
        </template>

        <template
          v-if="page.data.service_warning"
          #services-alert
        >
          <Alert
            color="info"
            class="mb-10"
          >
            <svg
              class="d-block me-2 text-secondary"
              width="16"
              height="16"
              aria-hidden="true"
            >
              <use xlink:href="#alert" />
            </svg>
            {{ page.data.service_warning }}
          </Alert>
        </template>
      </PagesContactsMap>
      <LazyBanner
        v-if="page.data.banner"
        heading-level="h2"
        :data="page.data.banner"
        class="mb-12 mb-md-20"
      >
        <template
          v-if="page.data.banner.tag.background && page.data.banner.tag.name"
          #tag
        >
          <Label
            text-color="var(--global-color-white)"
            :label="page.data.banner.tag.name"
            :background-color="page.data.banner.tag.background"
            :color="page.data.banner.tag.color"
          />
        </template>
      </LazyBanner>
    </ContainerFluid>
  </article>

  <IconsWrapper>
    <IconsSymbolChecked />
    <IconsSymbolCallcenter />
    <IconsSymbolInfo />
    <IconsSymbolAlert />
  </IconsWrapper>
</template>
